import { SplideProps, SplideSlide } from "@splidejs/react-splide";
import classcat from "classcat";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import css from "@is/components/PrimaryAttributeHeader/PrimaryAttributeHeader.module.css";
import { TranslationContext } from "@is/constants";
import { AttributeSetting } from "@is/types";

type Props = {
  attributes: AttributeSetting[];
  onAttributeClick: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  showSignUpButton: boolean;
};

const LANGUAGE_BUTTON_SELECTOR_WIDTH = "var(--space-20) + var(--space-1_5)";
const SIGN_UP_BUTTON_SELECTOR_WIDTH = "var(--space-60)";

const useManagePrimaryAttributeHeader = ({
  attributes,
  onAttributeClick,
  showSignUpButton,
}: Props) => {
  const { t } = useTranslation(TranslationContext.ProductListing);

  const editedAttributes = useMemo(
    () =>
      attributes.map(({ key, label, options }) => ({
        key,
        label,
        selected: !!options.length,
        selectedLabel: options.length
          ? options.map((option) => option.label).join(", ")
          : `${t("select")} ${label}`,
      })),
    [t, attributes]
  );

  const AttributeCarouselContent = useMemo(
    () => (
      <>
        {editedAttributes.map(
          ({ key, label, selected, selectedLabel }, index) => (
            <SplideSlide key={`${key}-${label}`}>
              <button
                key={`${key}-${label}`}
                data-attribute-index={index}
                data-name={key}
                onClick={onAttributeClick}
                className={classcat([
                  "text-body-lg",
                  css.primaryAttributeItem,
                  { [css.notFirst]: index > 0 },
                  { [css.selected]: selected },
                ])}
              >
                {selectedLabel}
              </button>
            </SplideSlide>
          )
        )}
      </>
    ),
    [editedAttributes, onAttributeClick]
  );

  const sliderAdditionalOptions: Partial<SplideProps> = {
    options: {
      width: `calc(100% - (${LANGUAGE_BUTTON_SELECTOR_WIDTH}) - (${
        showSignUpButton ? SIGN_UP_BUTTON_SELECTOR_WIDTH : 0
      }))`,
    },
  };

  return { AttributeCarouselContent, sliderAdditionalOptions };
};

export default useManagePrimaryAttributeHeader;
